import * as React from "react"

import { Button, type ButtonProps } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

export type IconButtonSize = "xs" | "compact" | "sm" | "md" | "lg"

const ICON_BUTTON_SIZE: Record<IconButtonSize, NonNullable<ButtonProps["size"]>> = {
  xs: "icon-xs",
  compact: "icon-compact",
  sm: "icon-sm",
  md: "icon",
  lg: "icon-lg",
}

/** The one documented "on" style for toggle-style icon buttons (`pressed`). */
export const PRESSED_CLASSES =
  "aria-pressed:bg-primary/15 aria-pressed:text-primary aria-pressed:hover:bg-primary/20 aria-pressed:hover:text-primary"

export interface IconButtonProps
  extends Omit<ButtonProps, "size" | "aria-label" | "aria-pressed"> {
  /** Accessible name; also the default tooltip text. Required: icon-only buttons must be named. */
  label: string
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>
  /** xs (24px) · compact (28px) · sm (32px, default) · md (36px) · lg (40px). */
  size?: IconButtonSize
  /** `false` hides the tooltip; a node replaces the label as tooltip content. */
  tooltip?: false | React.ReactNode
  tooltipSide?: React.ComponentProps<typeof TooltipContent>["side"]
  /** Keyboard hint shown in the tooltip, e.g. "⌘Z". */
  shortcut?: string
  /** Toggle state → `aria-pressed` + the shared active style. Omit for plain actions. */
  pressed?: boolean
  iconClassName?: string
}

/**
 * Icon-only button with a required accessible label and a tooltip. Forwards `ref` and every
 * Button prop, so it works as the child of `DropdownMenuTrigger asChild` / `PopoverTrigger asChild`.
 * Extra `children` (e.g. a count badge) render after the icon.
 */
function IconButton({
  label,
  icon: Icon,
  size = "sm",
  variant = "ghost",
  tooltip,
  tooltipSide,
  shortcut,
  pressed,
  className,
  iconClassName,
  children,
  ...props
}: IconButtonProps) {
  const button = (
    <Button
      type="button"
      variant={variant}
      size={ICON_BUTTON_SIZE[size]}
      aria-label={label}
      aria-pressed={pressed}
      data-slot="icon-button"
      className={cn(pressed !== undefined && PRESSED_CLASSES, className)}
      {...props}
    >
      <Icon className={iconClassName} aria-hidden="true" />
      {children}
    </Button>
  )

  if (tooltip === false) {
    return button
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={tooltipSide} className="flex items-center gap-2">
        <span>{tooltip ?? label}</span>
        {shortcut ? (
          <kbd className="rounded border border-background/30 px-1 font-sans text-[11px] leading-4 opacity-80">
            {shortcut}
          </kbd>
        ) : null}
      </TooltipContent>
    </Tooltip>
  )
}

export { IconButton }
